import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { KeyRound, Mail, CheckCircle, Loader2, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const ForgotPassword = () => {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  const navigate = useNavigate();

  // Request reset form state
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [demoToken, setDemoToken] = useState('');

  // Reset password form state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Indtast din email');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setEmailSent(true);
      toast.success('Reset-link sendt!');
      
      // Show demo token in development
      if (res.data.reset_token_demo) {
        setDemoToken(res.data.reset_token_demo);
        toast.info(`Demo token: ${res.data.reset_token_demo}`, { duration: 15000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Noget gik galt');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('Adgangskoden skal være mindst 6 tegn');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Adgangskoderne matcher ikke');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      
      setResetComplete(true);
      toast.success('Adgangskode nulstillet!');
      
      setTimeout(() => {
        navigate('/auth');
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset fejlede');
    } finally {
      setLoading(false);
    }
  };

  // Reset complete screen
  if (resetComplete) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-display font-bold text-foreground mb-2">
            Adgangskode Nulstillet!
          </h2>
          <p className="text-muted-foreground mb-4">
            Du kan nu logge ind med din nye adgangskode.
          </p>
          <Link to="/auth">
            <Button className="rounded-full">Gå til Login</Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  // Reset password form (when token is present)
  if (resetToken) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-card rounded-3xl border border-border p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <KeyRound className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-display font-bold text-foreground mb-2">
                Ny Adgangskode
              </h2>
              <p className="text-muted-foreground">
                Indtast din nye adgangskode
              </p>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label>Ny adgangskode</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mindst 6 tegn"
                    className="h-12 rounded-xl pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Bekræft adgangskode</Label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Gentag adgangskode"
                  className="h-12 rounded-xl"
                  data-testid="confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full bg-primary hover:bg-primary/90"
                data-testid="reset-password-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Nulstil Adgangskode'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/auth" className="text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-4 h-4 inline mr-1" />
                Tilbage til login
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Email sent confirmation
  if (emailSent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
        >
          <div className="bg-card rounded-3xl border border-border p-8">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-display font-bold text-foreground mb-2">
              Tjek din email
            </h2>
            <p className="text-muted-foreground mb-4">
              Vi har sendt et reset-link til <strong>{email}</strong>
            </p>
            
            {demoToken && (
              <div className="bg-muted rounded-xl p-4 mb-4 text-left">
                <p className="text-xs text-muted-foreground mb-2">Demo - Reset Link:</p>
                <Link 
                  to={`/forgot-password?token=${demoToken}`}
                  className="text-primary text-sm break-all hover:underline"
                >
                  Klik her for at nulstille adgangskode
                </Link>
              </div>
            )}

            <div className="space-y-3">
              <Button
                variant="outline"
                onClick={() => setEmailSent(false)}
                className="w-full rounded-full"
              >
                Send igen
              </Button>
              <Link to="/auth" className="block">
                <Button variant="ghost" className="w-full rounded-full">
                  Tilbage til login
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Request reset form
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-card rounded-3xl border border-border p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl font-display font-bold text-foreground mb-2">
              Glemt Adgangskode?
            </h2>
            <p className="text-muted-foreground">
              Indtast din email, så sender vi et reset-link
            </p>
          </div>

          <form onSubmit={handleRequestReset} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="din@email.dk"
                className="h-12 rounded-xl"
                data-testid="forgot-email-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-primary hover:bg-primary/90"
              data-testid="send-reset-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Send Reset-link'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/auth" className="text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              Tilbage til login
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
